import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getQuickBooksItems,
  syncQuickBooksItems,
  getTierMappings,
  saveTierMapping,
  autoMatchItems,
  aiMatchItems,
  applySuggestions,
  bulkSaveTierMappings,
  clearAllTierMappings,
  QuickBooksItem,
  QBTierMapping,
  TierFormula,
  AutoMatchProposal,
} from '../../services/quickbooksService';

const PRICE_TIERS = [
  { key: 'bag_mash', label: 'Bag M', short: 'BgM' },
  { key: 'bag_pellet', label: 'Bag P', short: 'BgP' },
  { key: 'byoc_mash', label: 'BYOC M', short: 'BM' },
  { key: 'byoc_pellet', label: 'BYOC P', short: 'BP' },
  { key: 'bulk_1000_mash', label: '1000 M', short: '1kM' },
  { key: 'bulk_1000_pellet', label: '1000 P', short: '1kP' },
  { key: 'bulk_2000_mash', label: '2000 M', short: '2kM' },
  { key: 'bulk_2000_pellet', label: '2000 P', short: '2kP' },
  { key: 'ftl', label: 'FTL', short: 'FTL' },
  { key: 'store_bag_mash', label: 'Str M', short: 'SM' },
  { key: 'store_bag_pellet', label: 'Str P', short: 'SP' },
] as const;

const ANIMAL_TYPES = ['POULTRY', 'SWINE', 'HORSE', 'RUMINANTS', 'SPECIAL'];
const GRADE_LABELS: Record<string, string> = { ESSENTIAL: 'Essential', PLUS: 'Plus', FIVE_STAR: 'Five Star' };

type MappingKey = string; // `${formulaId}:${priceTier}`

const QuickBooksItemMapping: React.FC = () => {
  const [qbItems, setQbItems] = useState<QuickBooksItem[]>([]);
  const [formulas, setFormulas] = useState<TierFormula[]>([]);
  const [mappings, setMappings] = useState<Record<MappingKey, QBTierMapping>>({});
  const [suggestions, setSuggestions] = useState<Record<MappingKey, AutoMatchProposal>>({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [aiMatching, setAiMatching] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [animalFilter, setAnimalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'mapped' | 'partial' | 'unmapped' | 'suggested'>('all');

  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownSearch, setDropdownSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsData, tierData] = await Promise.all([
        getQuickBooksItems({ active_only: true }),
        getTierMappings(),
      ]);
      setQbItems(Array.isArray(itemsData) ? itemsData : []);
      setFormulas(tierData.formulas || []);

      const mappingMap: Record<MappingKey, QBTierMapping> = {};
      for (const m of tierData.mappings || []) {
        mappingMap[`${m.formula_id}:${m.price_tier}`] = m;
      }
      setMappings(mappingMap);
    } catch (error) {
      console.error('Failed to load data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const result = await syncQuickBooksItems();
      setMessage({ type: 'success', text: `Synced ${result.synced_count} QB items` });
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to sync items' });
    } finally {
      setSyncing(false);
    }
  };

  const handleAutoMatch = async () => {
    setAutoMatching(true);
    setMessage(null);
    try {
      const result = await autoMatchItems();
      const suggMap: Record<MappingKey, AutoMatchProposal> = {};
      for (const p of result.proposed) {
        const key = `${p.formula_id}:${p.price_tier}`;
        // Don't suggest if already mapped
        if (!mappings[key]) {
          suggMap[key] = p;
        }
      }
      setSuggestions(suggMap);
      setMessage({
        type: 'success',
        text: `Found ${result.proposed_count} matches (${Object.keys(suggMap).length} new suggestions, ${result.unmatched_qb_count} unmatched QB items, ${result.unmatched_formulas_count} unmatched formulas)`
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Auto-match failed' });
    } finally {
      setAutoMatching(false);
    }
  };

  const handleAIMatch = async () => {
    setAiMatching(true);
    setMessage(null);
    try {
      const result = await aiMatchItems();
      const suggMap: Record<MappingKey, AutoMatchProposal> = {};
      for (const p of result.proposed) {
        const key = `${p.formula_id}:${p.price_tier}`;
        // Don't suggest if already mapped
        if (!mappings[key]) {
          suggMap[key] = p;
        }
      }
      setSuggestions(suggMap);
      setMessage({
        type: 'success',
        text: `Regex: ${result.regex_count} matches, AI: ${result.ai_count} matches. ${Object.keys(suggMap).length} new suggestions total. ${result.unmatched_formulas_count} formulas still unmatched.`
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'AI match failed. Check that ANTHROPIC_API_KEY is configured.' });
    } finally {
      setAiMatching(false);
    }
  };

  const handleAcceptSuggestion = async (key: MappingKey) => {
    const s = suggestions[key];
    if (!s) return;
    try {
      await saveTierMapping(s.formula_id, s.price_tier, s.qb_item_id, s.qb_item_name);
      setMappings(prev => ({
        ...prev,
        [key]: {
          id: 0, formula_id: s.formula_id, formula_name: s.formula_name,
          formula_code: '', formula_feed_grade: '', formula_animal_type: '',
          price_tier: s.price_tier, price_tier_display: '',
          qb_item_id: s.qb_item_id, qb_item_name: s.qb_item_name, match_method: 'auto',
        } as QBTierMapping,
      }));
      setSuggestions(prev => { const n = { ...prev }; delete n[key]; return n; });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save' });
    }
  };

  const handleAcceptAll = async () => {
    const high = Object.entries(suggestions)
      .filter(([, s]) => s.confidence === 'high')
      .map(([, s]) => ({
        formula_id: s.formula_id, price_tier: s.price_tier,
        qb_item_id: s.qb_item_id, qb_item_name: s.qb_item_name, match_method: 'auto',
      }));
    if (high.length === 0) {
      setMessage({ type: 'error', text: 'No high-confidence suggestions to accept' });
      return;
    }
    try {
      await bulkSaveTierMappings(high);
      setMessage({ type: 'success', text: `Saved ${high.length} high-confidence matches` });
      await loadData();
      setSuggestions({});
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to bulk save' });
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear ALL item mappings? This cannot be undone.')) return;
    try {
      const result = await clearAllTierMappings();
      setMappings({});
      setSuggestions({});
      setMessage({ type: 'success', text: `Cleared ${result.deleted} mappings` });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear' });
    }
  };

  const handleSelectQbItem = async (formulaId: number, tier: string, qbItem: QuickBooksItem | null) => {
    const key = `${formulaId}:${tier}`;
    setOpenDropdown(null);
    if (!qbItem) {
      try {
        await saveTierMapping(formulaId, tier, null);
        setMappings(prev => { const n = { ...prev }; delete n[key]; return n; });
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to remove mapping' });
      }
      return;
    }
    try {
      await saveTierMapping(formulaId, tier, qbItem.qb_id, qbItem.name);
      setMappings(prev => ({
        ...prev,
        [key]: {
          id: 0, formula_id: formulaId, formula_name: '',
          formula_code: '', formula_feed_grade: '', formula_animal_type: '',
          price_tier: tier, price_tier_display: '',
          qb_item_id: qbItem.qb_id, qb_item_name: qbItem.name, match_method: 'manual',
        } as QBTierMapping,
      }));
      // Clear suggestion if existed
      setSuggestions(prev => { const n = { ...prev }; delete n[key]; return n; });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save mapping' });
    }
  };

  // Compute stats
  const getFormulaStatus = (f: TierFormula) => {
    const tierKeys = Object.keys(f.has_tiers);
    if (tierKeys.length === 0) return 'unmapped';
    const mapped = tierKeys.filter(t => mappings[`${f.id}:${t}`]);
    if (mapped.length === tierKeys.length) return 'mapped';
    if (mapped.length > 0) return 'partial';
    const hasSugg = tierKeys.some(t => suggestions[`${f.id}:${t}`]);
    if (hasSugg) return 'suggested';
    return 'unmapped';
  };

  const filteredFormulas = formulas.filter(f => {
    if (searchTerm && !f.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (animalFilter && f.animal_type !== animalFilter) return false;
    if (statusFilter !== 'all') {
      const st = getFormulaStatus(f);
      if (statusFilter === 'suggested') return st === 'suggested' || Object.keys(suggestions).some(k => k.startsWith(`${f.id}:`));
      return st === statusFilter;
    }
    return true;
  });

  const mappedCount = formulas.filter(f => getFormulaStatus(f) === 'mapped').length;
  const partialCount = formulas.filter(f => getFormulaStatus(f) === 'partial').length;
  const unmappedCount = formulas.filter(f => getFormulaStatus(f) === 'unmapped').length;
  const suggestionCount = Object.keys(suggestions).length;

  // Group by animal type
  const grouped: Record<string, TierFormula[]> = {};
  for (const f of filteredFormulas) {
    const key = f.animal_type || 'OTHER';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(f);
  }

  if (loading) {
    return (
      <div style={s.container}>
        <h1 style={s.title}>QuickBooks Item Mapping</h1>
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={s.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={s.title}>QuickBooks Item Mapping</h1>
          <p style={{ color: '#6b7280', margin: '0.25rem 0 0' }}>
            Map each formula + price tier to a specific QB product for accurate invoices.
          </p>
        </div>
        <Link to="/settings/quickbooks" style={{ color: '#2D5A27', textDecoration: 'none', fontWeight: 500 }}>
          Back to QB Settings
        </Link>
      </div>

      {message && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem',
          backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${message.type === 'success' ? '#86efac' : '#fecaca'}`,
          color: message.type === 'success' ? '#166534' : '#dc2626',
        }}>
          {message.text}
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={s.card}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#166534' }}>{mappedCount}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Fully Mapped</div>
        </div>
        <div style={s.card}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#92400e' }}>{partialCount}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Partial</div>
        </div>
        <div style={s.card}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#991b1b' }}>{unmappedCount}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Unmapped</div>
        </div>
        <div style={s.card}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e40af' }}>{qbItems.length}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>QB Items</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button onClick={handleSync} disabled={syncing} style={s.btn}>
          {syncing ? 'Syncing...' : 'Sync QB Items'}
        </button>
        <button onClick={handleAIMatch} disabled={aiMatching || autoMatching} style={{ ...s.btn, backgroundColor: aiMatching ? '#e0e7ff' : '#eef2ff', color: '#4338ca', border: '1px solid #a5b4fc' }}>
          {aiMatching ? 'AI Matching...' : 'AI Match'}
        </button>
        <button onClick={handleAutoMatch} disabled={autoMatching || aiMatching} style={{ ...s.btn, backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #93c5fd' }}>
          {autoMatching ? 'Matching...' : 'Quick Match'}
        </button>
        {suggestionCount > 0 && (
          <button onClick={handleAcceptAll} style={{ ...s.btn, backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #86efac' }}>
            Accept All High ({Object.values(suggestions).filter(ss => ss.confidence === 'high').length})
          </button>
        )}
        {Object.keys(mappings).length > 0 && (
          <button onClick={handleClearAll} style={{ ...s.btn, backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5' }}>
            Clear All
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          type="text" placeholder="Search formulas..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', flex: 1, minWidth: '150px' }}
        />
        <select value={animalFilter} onChange={(e) => setAnimalFilter(e.target.value)}
          style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}>
          <option value="">All Animals</option>
          {ANIMAL_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}>
          <option value="all">All Status</option>
          <option value="mapped">Fully Mapped ({mappedCount})</option>
          <option value="partial">Partial ({partialCount})</option>
          <option value="unmapped">Unmapped ({unmappedCount})</option>
          {suggestionCount > 0 && <option value="suggested">Has Suggestions ({suggestionCount})</option>}
        </select>
      </div>

      {/* Main Table */}
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fff' }} ref={dropdownRef}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ ...s.th, minWidth: '180px', position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 2 }}>Formula</th>
              <th style={{ ...s.th, width: '50px' }}>Grade</th>
              {PRICE_TIERS.map(t => (
                <th key={t.key} style={{ ...s.th, minWidth: '100px', textAlign: 'center' }} title={t.label}>{t.short}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([animal, fList]) => (
              <React.Fragment key={animal}>
                <tr>
                  <td colSpan={2 + PRICE_TIERS.length} style={{
                    padding: '0.4rem 0.75rem', fontWeight: 700, fontSize: '0.8rem',
                    backgroundColor: '#f0fdf4', color: '#166534', borderBottom: '1px solid #e5e7eb',
                  }}>
                    {animal}
                  </td>
                </tr>
                {fList.map(formula => (
                  <tr key={formula.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ ...s.td, position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1, fontWeight: 500 }}>
                      {formula.name}
                    </td>
                    <td style={{ ...s.td, fontSize: '0.7rem', color: '#6b7280' }}>
                      {GRADE_LABELS[formula.feed_grade] || formula.feed_grade}
                    </td>
                    {PRICE_TIERS.map(tier => {
                      const key = `${formula.id}:${tier.key}`;
                      const mapping = mappings[key];
                      const suggestion = suggestions[key];
                      const hasTier = formula.has_tiers[tier.key];

                      if (!hasTier && tier.key !== 'ftl' && tier.key !== 'store_bag_mash' && tier.key !== 'store_bag_pellet') {
                        return <td key={tier.key} style={{ ...s.td, textAlign: 'center', color: '#d1d5db' }}>-</td>;
                      }

                      if (mapping) {
                        return (
                          <td key={tier.key} style={{ ...s.td, position: 'relative' }}>
                            <div
                              style={{
                                padding: '0.2rem 0.3rem', borderRadius: '4px', fontSize: '0.7rem',
                                backgroundColor: '#f0fdf4', border: '1px solid #86efac', cursor: 'pointer',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px',
                              }}
                              title={`${mapping.qb_item_name} (${mapping.match_method})\nClick to change`}
                              onClick={() => { setOpenDropdown(key); setDropdownSearch(''); }}
                            >
                              {mapping.qb_item_name}
                            </div>
                            {openDropdown === key && renderDropdown(formula.id, tier.key)}
                          </td>
                        );
                      }

                      if (suggestion) {
                        return (
                          <td key={tier.key} style={{ ...s.td, position: 'relative' }}>
                            <div style={{
                              padding: '0.2rem 0.3rem', borderRadius: '4px', fontSize: '0.7rem',
                              backgroundColor: suggestion.confidence === 'high' ? '#fffbeb' : '#fef3c7',
                              border: `1px solid ${suggestion.confidence === 'high' ? '#fcd34d' : '#fbbf24'}`,
                              display: 'flex', alignItems: 'center', gap: '0.2rem',
                            }}
                              title={`Suggested: ${suggestion.qb_item_name} (${suggestion.confidence}${suggestion.source === 'ai' ? ', AI' : ''})`}
                            >
                              {suggestion.source === 'ai' && (
                                <span style={{ fontSize: '0.55rem', color: '#4338ca', fontWeight: 700 }}>AI</span>
                              )}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, maxWidth: '70px' }}>
                                {suggestion.qb_item_name}
                              </span>
                              <span
                                onClick={() => handleAcceptSuggestion(key)}
                                style={{ cursor: 'pointer', color: '#166534', fontWeight: 700, fontSize: '0.75rem' }}
                                title="Accept"
                              >&#10003;</span>
                            </div>
                          </td>
                        );
                      }

                      // Empty - show clickable cell
                      return (
                        <td key={tier.key} style={{ ...s.td, position: 'relative' }}>
                          <div
                            onClick={() => { setOpenDropdown(key); setDropdownSearch(''); }}
                            style={{
                              padding: '0.2rem 0.3rem', borderRadius: '4px', fontSize: '0.7rem',
                              border: '1px dashed #d1d5db', color: '#9ca3af', cursor: 'pointer',
                              textAlign: 'center',
                            }}
                          >
                            +
                          </div>
                          {openDropdown === key && renderDropdown(formula.id, tier.key)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {filteredFormulas.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            {searchTerm ? 'No formulas match your search' : 'No formulas found'}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', fontSize: '0.75rem', color: '#6b7280', flexWrap: 'wrap' }}>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 2, marginRight: 4 }}></span> Mapped</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 2, marginRight: 4 }}></span> Suggested</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, backgroundColor: '#eef2ff', border: '1px solid #a5b4fc', borderRadius: 2, marginRight: 4, fontSize: '6px', textAlign: 'center', lineHeight: '8px', color: '#4338ca', fontWeight: 700 }}>AI</span> AI Match</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, border: '1px dashed #d1d5db', borderRadius: 2, marginRight: 4 }}></span> Empty</span>
        <span>- = No price tier</span>
      </div>
    </div>
  );

  function renderDropdown(formulaId: number, tier: string) {
    const filtered = qbItems.filter(item => {
      if (!dropdownSearch) return true;
      const s = dropdownSearch.toLowerCase();
      return item.name.toLowerCase().includes(s) || (item.description || '').toLowerCase().includes(s);
    }).slice(0, 25);

    return (
      <div style={{
        position: 'absolute', top: '100%', left: 0, zIndex: 100, width: '280px',
        backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '250px', overflow: 'hidden',
      }}>
        <div style={{ padding: '0.3rem', borderBottom: '1px solid #e5e7eb' }}>
          <input
            autoFocus
            type="text"
            placeholder="Search QB items..."
            value={dropdownSearch}
            onChange={(e) => setDropdownSearch(e.target.value)}
            style={{ width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {mappings[`${formulaId}:${tier}`] && (
            <div
              onMouseDown={(e) => { e.preventDefault(); handleSelectQbItem(formulaId, tier, null); }}
              style={{ padding: '0.35rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', color: '#991b1b', borderBottom: '1px solid #f3f4f6' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Remove mapping
            </div>
          )}
          {filtered.map(item => (
            <div
              key={item.qb_id}
              onMouseDown={(e) => { e.preventDefault(); handleSelectQbItem(formulaId, tier, item); }}
              style={{ padding: '0.35rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', borderBottom: '1px solid #f3f4f6' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ fontWeight: 500 }}>{item.name}</div>
              {item.unit_price && (
                <div style={{ fontSize: '0.68rem', color: '#6b7280' }}>
                  ${Number(item.unit_price).toFixed(2)} | {item.item_type}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '0.5rem', color: '#9ca3af', fontSize: '0.78rem', textAlign: 'center' }}>No matches</div>
          )}
        </div>
      </div>
    );
  }
};

const s: Record<string, React.CSSProperties> = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '1.5rem' },
  title: { fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: 0 },
  card: {
    padding: '0.75rem 1.25rem', backgroundColor: '#fff', borderRadius: '8px',
    border: '1px solid #e5e7eb', textAlign: 'center', minWidth: '80px',
  },
  btn: {
    padding: '0.4rem 0.75rem', fontSize: '0.85rem', fontWeight: 500,
    backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db',
    borderRadius: '6px', cursor: 'pointer',
  },
  th: {
    padding: '0.4rem 0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem',
    borderBottom: '2px solid #e5e7eb', color: '#374151', whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.3rem 0.4rem', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle',
  },
};

export default QuickBooksItemMapping;
